import React, { useState } from "react";
import api from "@/lib/api";
import { SLOTS, DEFAULT_OUTFIT, OutfitOverlay } from "./agentWardrobeItems.jsx";
import { paletteFor } from "./AgentFaceStage";

/**
 * AgentWardrobe — modal that lets the owner dress each agent.
 *
 * Owner asked: "when I click on one of the agents on the popup widget,
 * have a button that opens a mini wardrobe. List hat / jumper / jacket /
 * pants / watches / chains. Five items per category. When I click a
 * category, show pictures I can choose from."
 *
 * UX:
 *  - Left column: live preview of the mascot with the selected outfit
 *  - Right column: 6 slot tabs (Hat / Jumper / Jacket / Pants / Watch / Chain)
 *    + a grid of 5+ thumbnails (SVG previews) per slot. Click → applied.
 *  - SAVE button persists to /api/jarvis/agents/:id with field `outfit`.
 */
export default function AgentWardrobe({ agent, onClose, onSaved }) {
  const initial = (agent.outfit && typeof agent.outfit === "object")
    ? { ...DEFAULT_OUTFIT(), ...agent.outfit }
    : DEFAULT_OUTFIT();
  const [outfit, setOutfit] = useState(initial);
  const [activeSlot, setActiveSlot] = useState(SLOTS[0].key);
  const [busy, setBusy] = useState(false);

  const palette = paletteFor(agent.name);

  const pick = (slotKey, itemKey) => {
    setOutfit(o => ({ ...o, [slotKey]: itemKey }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/jarvis/agents/${agent.id}`, { outfit });
      onSaved?.({ ...agent, outfit });
      onClose?.();
    } catch (e) {
      console.warn("[wardrobe] save failed", e);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setOutfit(DEFAULT_OUTFIT());

  const slot = SLOTS.find(s => s.key === activeSlot);
  const items = Object.entries(slot.items);

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="agent-wardrobe-backdrop"
    >
      <div
        className="rc-panel p-5 max-w-4xl w-full mx-4 flex flex-col"
        style={{ maxHeight: "92vh", background: "rgba(2,4,12,0.96)" }}
        onClick={e => e.stopPropagation()}
        data-testid="agent-wardrobe-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="rc-label text-xs rc-glow-text">
            // WARDROBE · {agent.name?.toUpperCase()}
          </div>
          <button onClick={onClose} className="text-[var(--rc-gold)] opacity-70 hover:opacity-100 text-lg leading-none" data-testid="wardrobe-close">✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1 overflow-hidden">
          {/* LEFT — live preview */}
          <div className="flex flex-col items-center">
            <div className="w-full" style={{ maxWidth: 280, aspectRatio: "240/320" }}>
              <PreviewMascot palette={palette} outfit={outfit} agentName={agent.name}/>
            </div>
            <div className="text-[10px] text-[var(--rc-text-dim)] mt-2 text-center">
              Live preview · changes apply instantly
            </div>
            <button
              onClick={reset}
              className="mt-3 border border-[var(--rc-gold-dim)] text-[var(--rc-gold)] px-3 py-1.5 text-[10px] tracking-widest opacity-80 hover:opacity-100"
              data-testid="wardrobe-reset"
            >
              ↺ RESET TO NUDE
            </button>
          </div>

          {/* RIGHT — slot tabs + item grid */}
          <div className="flex flex-col overflow-hidden">
            {/* Slot tabs */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {SLOTS.map(s => (
                <button
                  key={s.key}
                  onClick={() => setActiveSlot(s.key)}
                  className="px-2.5 py-1 text-[10px] tracking-widest border"
                  style={{
                    borderColor: activeSlot === s.key ? "var(--rc-gold)" : "var(--rc-gold-dim)",
                    color: activeSlot === s.key ? "var(--rc-gold)" : "var(--rc-text-dim)",
                    background: activeSlot === s.key ? "rgba(255,184,0,0.08)" : "transparent",
                  }}
                  data-testid={`wardrobe-slot-${s.key}`}
                >
                  {s.label.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Items grid */}
            <div className="grid grid-cols-3 gap-2 overflow-y-auto pr-1" style={{ maxHeight: "55vh" }}>
              {items.map(([key, it]) => (
                <button
                  key={key}
                  onClick={() => pick(activeSlot, key)}
                  className="border p-2 flex flex-col items-center"
                  style={{
                    borderColor: outfit[activeSlot] === key ? "var(--rc-gold)" : "var(--rc-gold-dim)",
                    background: outfit[activeSlot] === key ? "rgba(255,184,0,0.1)" : "rgba(255,255,255,0.02)",
                  }}
                  data-testid={`wardrobe-pick-${activeSlot}-${key}`}
                >
                  <div style={{ width: 64, height: 84 }}>
                    <PreviewMascot
                      palette={palette}
                      outfit={{ ...DEFAULT_OUTFIT(), [activeSlot]: key }}
                      agentName={agent.name}
                      compact
                    />
                  </div>
                  <div className="text-[9px] tracking-wider text-center mt-1"
                       style={{ color: outfit[activeSlot] === key ? "var(--rc-gold)" : "var(--rc-text-dim)" }}>
                    {it.label}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="border border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)] px-4 py-2 text-xs tracking-widest opacity-80 hover:opacity-100"
            data-testid="wardrobe-cancel"
          >
            CANCEL
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="rc-btn px-5 py-2 text-xs"
            data-testid="wardrobe-save"
          >
            {busy ? "SAVING…" : "✓ SAVE OUTFIT"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Lightweight mascot renderer for the wardrobe preview ─────── */
function PreviewMascot({ palette, outfit, agentName, compact = false }) {
  const { glow, jumper, trim } = palette;
  const headStyle = ({ tom: "antenna", sarah: "headphones", eva: "hairtuft", scout: "cap", dobby: "horns" })[(agentName || "").toLowerCase()] || "default";
  return (
    <svg viewBox="0 0 240 320" width="100%" height="100%">
      <defs>
        <linearGradient id={`prev-jumper-${agentName}-${compact}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={jumper} stopOpacity="1"/>
          <stop offset="100%" stopColor={jumper} stopOpacity="0.78"/>
        </linearGradient>
      </defs>
      {/* ARMS */}
      <path d="M 58 200 Q 30 180 36 140 Q 40 110 52 102"
            fill="none" stroke={jumper} strokeWidth="14" strokeLinecap="round"/>
      <circle cx="52" cy="100" r="11" fill={trim}/>
      <circle cx="52" cy="100" r="7"  fill="#f4f4f5"/>
      <path d="M 182 200 Q 210 180 204 140 Q 200 110 188 102"
            fill="none" stroke={jumper} strokeWidth="14" strokeLinecap="round"/>
      <circle cx="188" cy="100" r="11" fill={trim}/>
      <circle cx="188" cy="100" r="7"  fill="#f4f4f5"/>
      {/* TORSO */}
      <path d="M 70 200 L 170 200 L 178 280 L 62 280 Z"
            fill={`url(#prev-jumper-${agentName}-${compact})`} stroke={trim} strokeWidth="2"/>
      <path d="M 88 200 L 152 200 L 148 212 L 92 212 Z" fill={trim} opacity="0.85"/>
      <ellipse cx="120" cy="285" rx="60" ry="6" fill="#0a0a14" opacity="0.5"/>
      {/* HEAD */}
      <ellipse cx="120" cy="110" rx="62" ry="56" fill="#e6e7ea" stroke="#1a1a24" strokeWidth="2"/>
      <rect x="68" y="92" width="104" height="44" rx="22" fill="#0e0e16" stroke="#1a1a24" strokeWidth="1.5"/>
      <rect x="92"  y="104" width="20" height="22" rx="10" fill={glow}/>
      <rect x="128" y="104" width="20" height="22" rx="10" fill={glow}/>
      <rect x="115" y="140" width="14" height="3" rx="1.5" fill="#0e0e16" stroke={trim} strokeWidth="1"/>
      {/* head flair — same logic as MascotBody but static */}
      {headStyle === "antenna" && (<>
        <line x1="120" y1="55" x2="120" y2="35" stroke={trim} strokeWidth="3"/>
        <circle cx="120" cy="32" r="5" fill={trim}/>
      </>)}
      {headStyle === "headphones" && (<>
        <path d="M 65 105 Q 120 50 175 105" fill="none" stroke={trim} strokeWidth="4"/>
        <ellipse cx="62" cy="110" rx="9" ry="14" fill={trim}/>
        <ellipse cx="178" cy="110" rx="9" ry="14" fill={trim}/>
      </>)}
      {headStyle === "hairtuft" && (<>
        <path d="M 90 70 Q 120 40 150 70" fill={trim}/>
      </>)}
      {headStyle === "cap" && (<>
        <path d="M 70 92 Q 120 60 170 92 L 170 86 Q 120 50 70 86 Z" fill={trim}/>
      </>)}
      {headStyle === "horns" && (<>
        <path d="M 90 64 L 96 44 L 100 64" fill={trim}/>
        <path d="M 140 64 L 144 44 L 150 64" fill={trim}/>
      </>)}
      {/* OUTFIT OVERLAY */}
      <OutfitOverlay outfit={outfit} palette={palette}/>
    </svg>
  );
}
